/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { getUserId } from "Models/User";
import {
  A_minus,
  A_plus,
  AB_minus,
  AB_plus,
  B_minus,
  B_plus,
  O_minus,
  O_plus,
} from "assets/bloodtypes";
import { Button, Card, Input, Progress, Stack } from "components";
import useCompanion from "hooks/useCompanion";
import useTryCatch from "hooks/useTryCatch";
import { updatePreferences } from "lib/preferencesApi";
import { useParams } from "react-router-dom";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import useMedicalConditions from "./useMedicalConditions";
import { useEffect, useRef } from "react";
import useRouting from "hooks/useRouting";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import usePreviousRoute from "utils/usePreviousRoute";
import { useTranslation } from "react-i18next";

import { pushToDataLayer } from "utils/gtm";

const MedicalConditions = () => {
  const { t } = useTranslation();
  const { userData, setLoading, isLoading } = useUser();
  const { userId } = useParams();
  const [companion, setCompanion] = useCompanion(userId);
  const { navigateWithEvent, eventId } = useEventRouting();
  const { shouldEnterDonePage } = useRouting();
  const {
    medicalConditions,
    setMedicalConditions,
    spcialAssistanc,
    handleOnChange,
    setSpecialAssistance,
    formData,
  } = useMedicalConditions(companion);

  const goBack = usePreviousRoute();

  const {
    progressBarValues,
    getNextStepName,
    getPreviousStepName,
    setCurrentStep,
  } = usePrefNavigator();
  useEffect(() => {
    setCurrentStep("medicalConditions");
  }, []);

  const bloodTypeOptions = [
    { key: "O_plus", label: t("medicalConditions.O_plus") },
    { key: "O_minus", label: t("medicalConditions.O_minus") },
    { key: "A_plus", label: t("medicalConditions.A_plus") },
    { key: "A_minus", label: t("medicalConditions.A_minus") },
    { key: "B_plus", label: t("medicalConditions.B_plus") },
    { key: "B_minus", label: t("medicalConditions.B_minus") },
    { key: "AB_plus", label: t("medicalConditions.AB_plus") },
    { key: "AB_minus", label: t("medicalConditions.AB_minus") },
    { key: "notSure", label: t("medicalConditions.notSure") },
  ];

  const onContinue = () => {
    const nextStepName = getNextStepName();
    if (nextStepName && ROUTES.choices[nextStepName]) {
      const routePath = ROUTES.choices[nextStepName](userId);
      navigateWithEvent(routePath);
    } else {
      return null;
    }
  };

  const onGoBack = () => {
    pushToDataLayer({
      event: "preferences_back",
      screen_name: "medical conditions",
      journey_step: "medical conditions",
    });
    const previousStepName = getPreviousStepName();
    if (previousStepName == null) {
      navigateWithEvent(ROUTES.choices.base(userId));
      return;
    }
    goBack();
  };

  const handleOnSubmit = useTryCatch(async (e) => {
    e.preventDefault();
    pushToDataLayer({
      event: "save_attendance_form",
      screen_name: "attendance form",
      journey_step: "attendance form",
    });
    const bloodType = Object.keys(formData).find(
      (key) => formData[key].state === "success",
    );

    setLoading(true);
    const id = getUserId(companion) || userId;
    await updatePreferences(
      id,
      {
        isCompanion: id !== getUserId(userData),
        blood_type: bloodType,
        medical_conditions: medicalConditions,
        special_medical_assistance: spcialAssistanc,
      },
      eventId,
    );
    setLoading(false);

    const newCompanions = setCompanion({
      ...companion,
      blood_type: bloodType,
      medical_conditions: medicalConditions,
      special_medical_assistance: spcialAssistanc,
    });

    if (onContinue === null) {
      const goTo = await shouldEnterDonePage(newCompanions);
      navigateWithEvent(goTo);
    } else {
      onContinue();
    }
  }, setLoading);

  const formRef = useRef(null);

  const MedicalConditionsIcons = (name) =>
    ({
      A_plus: A_plus,
      A_minus: A_minus,
      B_plus: B_plus,
      B_minus: B_minus,
      AB_plus: AB_plus,
      AB_minus: AB_minus,
      O_plus: O_plus,
      O_minus: O_minus,
      other: null,
    })[name];
  return (
    <PageLayout
      title={t("medicalConditions.title")}
      note={t("medicalConditions.description")}
      hasPolicy={false}
      hasGoBack
      goBackMethod={onGoBack}
      hasProgressBar
      progressBarValues={progressBarValues}
    >
      <Stack component="article" className="mt-10 max-w-[400px] mx-auto">
        <form
          ref={formRef}
          onSubmit={handleOnSubmit}
          className="flex flex-col justify-between sm:justify-center min-h-full gap-y-10"
        >
          <Input
            placeholder={t("medicalConditions.medicalConditionsPlaceholder")}
            label={t("medicalConditions.title")}
            type="textarea"
            value={medicalConditions}
            rows={3}
            onChange={(e) => {
              setMedicalConditions(e.target.value);
            }}
          />
          <div className="grid grid-cols-3 gap-2  mb-2.5">
            {bloodTypeOptions.map((option) => (
              <Card
                title={option.key === "notSure" ? option.label : ""}
                key={option.key}
                variant="allergy"
                media={MedicalConditionsIcons(option.key)}
                state={formData[option.key]?.state}
                onClick={() => handleOnChange(option.key)}
              />
            ))}
          </div>

          <Input
            placeholder={t(
              "medicalConditions.specialAssistanceNeededPlaceholder",
            )}
            label={t("medicalConditions.specialAssistanceLabel")}
            type="textarea"
            rows={3}
            value={spcialAssistanc}
            onChange={(e) => {
              setSpecialAssistance(e.target.value);
            }}
          />
          <Stack>
            <div className="flex sm:hidden gap-x-0.5">
              {progressBarValues.map((value, index) => (
                <Progress key={index} value={value} />
              ))}
            </div>
            <div className="mt-2 w-full">
              <Button
                variant="primary"
                onClick={(e) => formRef.current?.requestSubmit()}
                isLoading={isLoading}
                fullWidth
              >
                {t("medicalConditions.continueButtonLabel")}
              </Button>
            </div>
          </Stack>
        </form>
      </Stack>
    </PageLayout>
  );
};
export default MedicalConditions;
